import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import tippy, {MultipleTargets} from 'tippy.js';
import Decimal, {DecimalSource} from './break_infinity';
import {ICostEffect} from './interfaces/costeffect';
import {IToolTippy} from './mixins/tooltippy';
import {ri} from './runner-idle';

export const findAllDeep = (
  parent: Element,
  selectors: string,
  depth = null
) => {
  const nodes: Set<string | Element | Node> = new Set();
  const currentDepth = 1;
  const recordResult = (nodesArray: MultipleTargets) => {
    for (const node of nodesArray) {
      nodes.add(node);
    }
  };
  const recursiveSeek = (_parent: Element) => {
    // check for selectors in lightdom
    recordResult(_parent.querySelectorAll(selectors));
    if (_parent.shadowRoot) {
      // check for selectors in shadowRoot
      recordResult(_parent.shadowRoot.querySelectorAll(selectors));
      // look for nested components with shadowRoots
      for (const child of [..._parent.shadowRoot.querySelectorAll('*')].filter(
        (i) => i.shadowRoot
      )) {
        // make sure we haven't hit our depth limit
        if (depth === null || currentDepth < depth) {
          recursiveSeek(child);
        }
      }
    }
  };
  recursiveSeek(parent);
  return nodes;
};
export const formatCostEffect: (
  item: ICostEffect &
    IToolTippy & {length?: number; chance?: (step: number) => Decimal | number},
  quantity?: number
) => string = (item, quantity = 1) => {
  // if (item.tooltip) {
  //   setTimeout(() => {
  //     const oldCostTooltips = item.costTooltips;
  //     const oldEffectTooltips = item.effectTooltips;
  //     const costTooltips = tippy(
  //       item.tooltip.popper.querySelectorAll('[data-cost-string]')!,
  //       {
  //         duration: 0,
  //         delay: 0,
  //         allowHTML: true,
  //       }
  //     );
  //     item.costTooltips = costTooltips;
  //     const effectTooltips = tippy(
  //       item.tooltip.popper.querySelectorAll('[data-effect-string]')!,
  //       {
  //         duration: 0,
  //         delay: 0,
  //         allowHTML: true,
  //       }
  //     );
  //     item.effectTooltips = effectTooltips;
  //     item.costTooltips.forEach((tooltip) => {
  //       tooltip.setContent(
  //         `${[...item.cost].map(([, {string}]) => string?.(-1, 0))}`
  //       );
  //     });
  //     effectTooltips.forEach((tooltip) => {
  //       tooltip.setContent(
  //         `${[...item.effect]
  //           .filter(([k]) => tooltip.reference.classList.contains(k))
  //           .map(([, {string}]) => string?.(-1, 0))}`
  //       );
  //     });
  //     if (oldCostTooltips) {
  //       oldCostTooltips.forEach((tooltip) => {
  //         if (!costTooltips.includes(tooltip) && !tooltip.popper.isConnected)
  //           tooltip.destroy();
  //       });
  //     }
  //     if (oldEffectTooltips) {
  //       oldEffectTooltips.forEach((tooltip) => {
  //         if (!effectTooltips.includes(tooltip) && !tooltip.popper.isConnected)
  //           tooltip.destroy();
  //       });
  //     }
  //   }, 0);
  // }
  return `<span class='costeffect'>${
    item.cost.size > 0
      ? `<span style="font-size: 1.2em">Cost:</span><br>${[...item.cost]
          // .filter(([k, v]) => ri.get(k)?.unlocked)
          .map(([k, v]) => {
            const costAmount = new Decimal(v.value(-1, 0)).times(quantity);
            return `<span class='fancy ${k}' data-cost-string>${
              costAmount.lt(0) ? '+' : ''
            }${formatEng(costAmount.abs())}</span> ${ri.get(k).displayName}
            ${
              ri.preferences.showDetails && v.string?.(-1, 0)
                ? `<span class='subheader'>[${v.string?.(-1, 0)}]</span>`
                : ''
            }`;
          })
          .join('<br>')} `
      : ''
  } ${
    item.effect.size > 0
      ? `${
          item.cost.size > 0 ? '<br>' : ''
        }<span style="font-size: 1.2em">Effect:</span><br>${[...item.effect]
          .filter(([k, v]) => ri.get(k)?.unlocked)
          .filter(([k, v]) => !ri.get(k)?.tags?.has('hide-effect'))
          .map(
            ([k, v]) =>
              `<span class='fancy ${k}' data-effect-string>${formatEng(
                new Decimal(v.value(-1, 0)).times(quantity)
              )}</span> ${ri.get(k).displayName}
            ${
              ri.preferences.showDetails && v.string?.(-1, 0)
                ? `<span class='subheader'>[${v.string?.(-1, 0)}]</span>`
                : ''
            }`
          )
          .join('<br>')}
          `
      : ''
  }<br>Length: ${formatTime((item.length ?? 1) * quantity)}
  ${
    item.chance
      ? `<br>Chance of success: ${formatEng(
          new Decimal(item.chance(0)).times(100),
          3
        )}% ${
          ri.preferences.showDetails && item.chanceString
            ? `<span class='subheader'>[${item.chanceString(-1, 0)}]</span>`
            : ''
        }`
      : ''
  }</span>`;
};
export const logisticChance: (
  input: DecimalSource,
  steepness: DecimalSource,
  midpoint: DecimalSource
) => Decimal = (input, steepness, midpoint) => {
  input = new Decimal(input);
  steepness = new Decimal(steepness);
  midpoint = new Decimal(midpoint);
  return new Decimal(1).div(
    input.minus(midpoint).times(steepness).times(-1).exp().plus(1)
  );
};
export const formatEng = (num: DecimalSource, places = 4) => {
  const decimal = new Decimal(num);
  const {mantissa, exponent} = decimal;
  // Anything less than 1 should be shown
  if (exponent < 0)
    return decimal.toFixed(places - exponent).replace(/0+$/, '');
  // Less than 100,000, just show in full
  if (exponent < 5) {
    if (mantissa % 1 == 0) {
      return decimal.toFixed(0);
    }
    return decimal.toFixed(Math.max(0, places - exponent));
  }
  return `${(mantissa * Math.pow(10, exponent % 3)).toPrecision(4)}e${
    Math.floor(exponent / 3) * 3
  }`;
};

export const formatResourceValue = (value: DecimalSource) => formatEng(value);
export const formatResourceCapacity = (capacity: DecimalSource) =>
  formatEng(capacity);

export const formatStat = (stat: DecimalSource) => {
  return formatEng(stat);
};
export const formatStatDelta = (delta: Decimal, places = 4) => {
  let res = '';
  if (delta.eq(0)) return res;
  if (delta.exponent >= -4) {
    res = formatEng(delta, places - Math.min(places, -delta.exponent));
  }
  if (delta.lt(0)) return `-${res.slice(1)}`;
  return `+${res}`;
};

export const formatDelta = (delta: Decimal, places = 4) => {
  if (delta.eq(0)) return '';
  if (delta.lt(0)) return formatEng(delta, places);
  return `+${formatEng(delta, places)}`;
};

export const formatTime = (seconds: DecimalSource, short = false) => {
  seconds = new Decimal(seconds);
  let result = '';
  const places = seconds.lt(1) ? 0 : 0;
  const minutes = seconds.div(60).floor();
  const hours = minutes.div(60).floor();
  const days = hours.div(24).floor();
  const years = days.div(365).floor();
  if (years.gte(1)) result += `${years.toFixed(0)}${short ? ':' : 'y'}`;
  if (days.gte(1))
    result += `${days.minus(years.times(365)).toFixed(0)}${short ? ':' : 'd'}`;
  if (hours.gte(1))
    result += `${hours.minus(days.times(24)).toFixed(0)}${short ? ':' : 'h'}`;
  if (minutes.gte(1))
    result += `${minutes.minus(hours.times(60)).toFixed(0)}${
      short ? ':' : 'm'
    }`;
  return (result += `${seconds.minus(minutes.times(60)).toFixed(places)}${
    short ? '' : 's'
  }`);
};
export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const fancy = (name: string, string = '', invert = false) => {
  if (string == '') string = capitalizeFirstLetter(name);
  return `<span class='fancy ${
    invert ? 'invert' : ''
  } ${name}'>${string}</span>`;
};

declare type SummableProperty = 'uses' | 'timeSpent';

export function sumActivitiesByTag(tag: string, prop: SummableProperty) {
  return activitiesByTag(tag).reduce((acc, activity) => {
    if (!Reflect.has(activity, prop)) return acc;
    return acc.add(activity[prop]);
  }, new Decimal(0));
}

export function activitiesByTag(tag: string) {
  return Object.values(ri.activities).filter((activity) =>
    activity.tags.has(tag)
  );
}
